import {NavigationProp, ParamListBase} from '@react-navigation/native';

import {AppRoute} from '../models/AppRoute';
import type {RewardsStore} from '../stores/rewardsStore';
import {UserStore} from '../stores/userStore';
import {Analytics} from '../utils/analytics/analytics';
import {EventName} from '../utils/analytics/events';
import {logError} from '../utils/errorLogging';
import {logout as logoutOneSignal} from '../utils/onesignal';

const analyticsLogoutAndClearSession = () => {
  Analytics.logEvent({
    eventName: EventName.Logout,
    parameters: {},
  });
  Analytics.clearUserSession();
};

export const handleLogoutPress = async (
  navigation: NavigationProp<ParamListBase>,
  userStore: UserStore,
) => {
  await userStore.logout();
  analyticsLogoutAndClearSession();
  navigation.navigate(AppRoute.ROOT, {
    screen: AppRoute.MAIN,
    params: {screen: AppRoute.HOME},
  });
};

/**
 * DIS-1826: sign-out driven by a journey (for example the end of rewards
 * sign-up), not by the user pressing Logout, so no EventName.Logout is sent.
 * Never rejects. Postcondition: userStore and rewardsStore are both signed out.
 *
 * apiService.logout catches a failed POST /ecommerce/logout, logs it and
 * resolves, so an API failure does not reject userStore.logout; its local
 * cleanup still runs. The failed server-side revocation is not retried. That
 * gap predates DIS-1826. The try/catch below guards only non-API failures,
 * such as storage or the rootStore.logoutUser chain.
 */
export const forceSignOut = async (
  userStore: UserStore,
  rewardsStore: RewardsStore,
): Promise<void> => {
  if (userStore.isLoggedIn) {
    try {
      await userStore.logout();
    } catch (error) {
      // A non-API failure (storage, rootStore.logoutUser); an API failure
      // never reaches here, because apiService.logout swallows it.
      logError(error, 'forceSignOut.logout');
      try {
        // Local-only cleanup, the same path rootStore.passiveLogout uses.
        await userStore.logout({callApi: false});
      } catch (localError) {
        logError(localError, 'forceSignOut.localLogout');
      }
    }
    Analytics.clearUserSession();
  } else {
    // Lite member: no ecommerce session, so userStore.logout never runs.
    // The card reaction may already have called OneSignal.login.
    try {
      await logoutOneSignal();
    } catch (error) {
      logError(error, 'forceSignOut.oneSignalLogout');
    }
  }

  // Always: covers the lite member with no session, and a userStore.logout
  // that threw before rootStore.logoutUser reached rewardsStore.logout.
  try {
    await rewardsStore.logout();
  } catch (error) {
    logError(error, 'forceSignOut.rewardsLogout');
  }
};
